import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Paper,
  Divider,
  useTheme,
  alpha,
  Tabs,
  Tab,
  TextField,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Stack,
  ButtonGroup,
  Tooltip,
} from "@mui/material";
import {
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreIcon,
  FormatListNumbered,
  DoneAll,
  Error,
  ContentCopy,
  Update,
  ErrorOutline,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import {
  getMyntraMarketingList,
  getMyntraMarketingHistoryList,
} from "app/redux/slice/myntraSlice";
import { Table as CustomTable } from "app/components";
import { usePagination } from "app/hooks/usePagination";
import Loader from "@jumbo/components/Loader";
import moment from "moment";
import { LocalizationProvider, DateRangePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import { userType } from "app/helpers/userType";

const QUICK_RANGES = [
  {
    key: "today",
    label: "Today",
    getRange: () => [
      moment().startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "7d",
    label: "Last 7D",
    getRange: () => [
      moment().subtract(6, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "30d",
    label: "Last 30D",
    getRange: () => [
      moment().subtract(29, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "ytd",
    label: "YTD",
    getRange: () => [
      moment().startOf("year").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
];

const statusOptions = [
  { value: "completed", label: "Completed", color: "success" },
  { value: "failed", label: "Failed", color: "error" },
  { value: "partial", label: "Partial", color: "warning" },
];

const MyntraMarketingExpenses = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { isMA } = userType();

  const [activeTab, setActiveTab] = useState("list");

  const myntraData = useSelector((state) => state?.myntra);

  // Marketing List state
  const marketingList = myntraData?.marketing?.list ?? [];
  const [listDateRange, setListDateRange] = useState([null, null]);
  const [tempListDateRange, setTempListDateRange] = useState([null, null]);

  const {
    page: listPage,
    rowsPerPage: listRowsPerPage,
    handleChangePage: listHandleChangePage,
    handleChangeRowsPerPage: listHandleChangeRowsPerPage,
  } = usePagination({ id: "myntra-marketing-list" });

  const [listOrder, setListOrder] = useState("desc");
  const [listOrderBy, setListOrderBy] = useState("date");

  // History state
  const historyList = myntraData?.marketingHistory?.list ?? [];
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({ status: "" });
  const [dateRange, setDateRange] = useState([null, null]);
  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const [tempFilters, setTempFilters] = useState({ status: "" });
  const [tempDateRange, setTempDateRange] = useState([null, null]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [rowDetails, setRowDetails] = useState(null);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "myntra-marketing-history" });

  // Fetch marketing list
  useEffect(() => {
    if (activeTab === "list") {
      fetchMarketingList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    listPage,
    listRowsPerPage,
    listDateRange,
    listOrder,
    listOrderBy,
  ]);

  const fetchMarketingList = () => {
    const params = {
      page: listPage,
      perPage: listRowsPerPage,
      field: listOrderBy,
      sort: listOrder === "desc" ? -1 : 1,
    };
    if (listDateRange[0] !== null && listDateRange[1] !== null) {
      params.fromDate = listDateRange[0];
      params.toDate = listDateRange[1];
    }
    dispatch(getMyntraMarketingList(params));
  };

  // Fetch history
  useEffect(() => {
    if (activeTab === "history") {
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    page,
    rowsPerPage,
    searchTerm,
    filters,
    dateRange,
    order,
    orderBy,
  ]);

  const fetchHistory = () => {
    const params = {
      page,
      perPage: rowsPerPage,
      search: searchTerm.trim(),
    };
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        params[key] = value;
      }
    });
    if (dateRange[0] !== null && dateRange[1] !== null) {
      params.fromDate = dateRange[0];
      params.toDate = dateRange[1];
    }
    dispatch(getMyntraMarketingHistoryList(params));
  };

  const applyListFilters = () => {
    setListDateRange(tempListDateRange);
    listHandleChangePage(0, 0);
  };

  const clearListFilters = () => {
    setTempListDateRange([null, null]);
    setListDateRange([null, null]);
    listHandleChangePage(0, 0);
  };

  const applyHistoryFilters = () => {
    setSearchTerm(tempSearchTerm);
    setFilters({ ...tempFilters });
    setDateRange(tempDateRange);
    handleChangePage(0, 0);
  };

  const clearHistoryFilters = () => {
    setTempSearchTerm("");
    setSearchTerm("");
    setTempFilters({ status: "" });
    setFilters({ status: "" });
    setTempDateRange([null, null]);
    setDateRange([null, null]);
    handleChangePage(0, 0);
  };

  const getStatusChip = (status) => {
    const cfg = statusOptions.find((o) => o.value === status);
    return (
      <Chip
        label={cfg?.label || status}
        color={cfg?.color || "default"}
        size="small"
      />
    );
  };

  const formatNumber = (val) =>
    val != null
      ? Number(val).toLocaleString("en-IN", { maximumFractionDigits: 2 })
      : "-";

  const formatCurrency = (val) =>
    val != null
      ? `₹${Number(val).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`
      : "-";

  // Marketing list columns
  const marketingColumns = [
    {
      id: "date",
      label: "Date",
      sort: false,
      render: (row) => moment(row?.date).format("DD/MM/YYYY"),
    },
    {
      id: "spend",
      label: "Spend",
      sort: false,
      isTotal: true,
      format: (val) => formatCurrency(val),
      render: (row) => formatCurrency(row?.spend),
    },
    {
      id: "impressions",
      label: "Impressions",
      sort: false,
      isTotal: true,
      format: (val) => formatNumber(val),
      render: (row) => formatNumber(row?.impressions),
    },
    {
      id: "clicks",
      label: "Clicks",
      sort: false,
      isTotal: true,
      format: (val) => formatNumber(val),
      render: (row) => formatNumber(row?.clicks),
    },
    {
      id: "ctr",
      label: "CTR",
      sort: false,
      render: (row) => `${formatNumber(row?.ctr)}%`,
    },
    {
      id: "unitsSold",
      label: "Units Sold",
      sort: false,
      isTotal: true,
      format: (val) => formatNumber(val),
      render: (row) => formatNumber(row?.unitsSold),
    },
    {
      id: "revenue",
      label: "Revenue",
      sort: false,
      isTotal: true,
      format: (val) => formatCurrency(val),
      render: (row) => formatCurrency(row?.revenue),
    },
    {
      id: "totalROI",
      label: "ROI",
      sort: false,
      render: (row) => formatNumber(row?.totalROI),
    },
  ];

  // History columns
  const historyColumns = [
    {
      id: "originalFileName",
      label: "File Name",
      sort: false,
      render: (row) => (
        <Tooltip title={row?.originalFileName}>
          <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
            {row?.originalFileName}
          </Typography>
        </Tooltip>
      ),
    },
    {
      id: "createdAt",
      label: "Date",
      sort: false,
      render: (row) => moment(row?.createdAt).format("DD/MM/YYYY hh:mm A"),
    },
    { id: "totalRecords", label: "Total", sort: false },
    { id: "duplicateRecords", label: "Duplicate", sort: false },
    { id: "successfulImports", label: "Successful", sort: false },
    { id: "failedImports", label: "Failed", sort: false },
    { id: "skipRecords", label: "Skip", sort: false },
    { id: "updatedRecords", label: "Updated", sort: false },
    {
      id: "status",
      label: "Status",
      sort: false,
      render: (row) => getStatusChip(row?.status),
    },
    {
      id: "actions",
      label: "Actions",
      align: "right",
      render: (row) => (
        <IconButton
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
            setSelectedRow(row);
          }}
        >
          <MoreIcon />
        </IconButton>
      ),
    },
  ];

  const statCards = [
    {
      label: "Total",
      value: rowDetails?.totalRecords,
      icon: <FormatListNumbered />,
    },
    {
      label: "Success",
      value: rowDetails?.successfulImports,
      icon: <DoneAll />,
      color: "success",
    },
    {
      label: "Updated",
      value: rowDetails?.updatedRecords,
      icon: <Update />,
      color: "info",
    },
    {
      label: "Duplicates",
      value: rowDetails?.duplicateRecords,
      icon: <ContentCopy />,
      color: "warning",
    },
    {
      label: "Skipped",
      value: rowDetails?.skipRecords,
      icon: <ErrorOutline />,
      color: "secondary",
    },
    {
      label: "Failed",
      value: rowDetails?.failedImports,
      icon: <Error />,
      color: "error",
    },
  ];

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Myntra Marketing & Expenses
      </Typography>

      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab value="list" label="Marketing List" />
        <Tab value="history" label="Import History" />
      </Tabs>

      {/* ──────────────── MARKETING LIST TAB ──────────────── */}
      {activeTab === "list" && (
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateRangePicker
                      startText="From Date"
                      endText="To Date"
                      inputFormat="dd/MM/yyyy"
                      value={tempListDateRange}
                      onChange={setTempListDateRange}
                      renderInput={(
                        { inputProps, ...startProps },
                        endProps,
                      ) => {
                        const startValue = inputProps.value;
                        delete inputProps.value;
                        return (
                          <TextField
                            {...startProps}
                            inputProps={inputProps}
                            value={`${startValue || "DD/MM/YYYY"} - ${
                              endProps.inputProps.value || "DD/MM/YYYY"
                            }`}
                            fullWidth
                            size="small"
                          />
                        );
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={3.5}>
                  <ButtonGroup size="small" variant="outlined" fullWidth>
                    {QUICK_RANGES.map((r) => (
                      <Button
                        key={r.key}
                        onClick={() => setTempListDateRange(r.getRange())}
                      >
                        {r.label}
                      </Button>
                    ))}
                  </ButtonGroup>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      onClick={applyListFilters}
                      fullWidth
                      size="small"
                    >
                      Apply Filters
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={clearListFilters}
                      startIcon={<RefreshIcon />}
                      fullWidth
                      size="small"
                    >
                      Clear
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {myntraData?.marketing?.loading ? (
            <Loader />
          ) : (
            <Card>
              <CardContent>
                <CustomTable
                  columns={marketingColumns}
                  rows={marketingList}
                  page={listPage}
                  rowsPerPage={listRowsPerPage}
                  count={myntraData?.marketing?.total || marketingList.length}
                  Order={listOrder}
                  OrderBy={listOrderBy}
                  sortData={(o, ob) => {
                    setListOrder(o);
                    setListOrderBy(ob);
                  }}
                  handleChangePage={listHandleChangePage}
                  handleChangeRowsPerPage={listHandleChangeRowsPerPage}
                  loading={myntraData?.marketing?.loading}
                  showTotalInFooter
                />
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {/* ──────────────── HISTORY TAB ──────────────── */}
      {activeTab === "history" && (
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by file name..."
                    value={tempSearchTerm}
                    onChange={(e) => setTempSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2.75}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateRangePicker
                      startText="From Date"
                      endText="To Date"
                      inputFormat="dd/MM/yyyy"
                      value={tempDateRange}
                      onChange={setTempDateRange}
                      renderInput={(
                        { inputProps, ...startProps },
                        endProps,
                      ) => {
                        const startValue = inputProps.value;
                        delete inputProps.value;
                        return (
                          <TextField
                            {...startProps}
                            inputProps={inputProps}
                            value={`${startValue || "DD/MM/YYYY"} - ${
                              endProps.inputProps.value || "DD/MM/YYYY"
                            }`}
                            fullWidth
                            size="small"
                          />
                        );
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={3.5}>
                  <ButtonGroup size="small" variant="outlined" fullWidth>
                    {QUICK_RANGES.map((r) => (
                      <Button
                        key={r.key}
                        onClick={() => setTempDateRange(r.getRange())}
                      >
                        {r.label}
                      </Button>
                    ))}
                  </ButtonGroup>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={tempFilters.status}
                      onChange={(e) =>
                        setTempFilters((p) => ({
                          ...p,
                          status: e.target.value,
                        }))
                      }
                      label="Status"
                    >
                      <MenuItem value="">All</MenuItem>
                      {statusOptions.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      onClick={applyHistoryFilters}
                      fullWidth
                      size="small"
                    >
                      Apply Filters
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={clearHistoryFilters}
                      startIcon={<RefreshIcon />}
                      fullWidth
                      size="small"
                    >
                      Clear
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {myntraData?.marketingHistory?.loading ? (
            <Loader />
          ) : (
            <Card>
              <CardContent>
                <CustomTable
                  columns={historyColumns}
                  rows={historyList}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  count={
                    myntraData?.marketingHistory?.total || historyList.length
                  }
                  Order={order}
                  OrderBy={orderBy}
                  sortData={(o, ob) => {
                    setOrder(o);
                    setOrderBy(ob);
                  }}
                  handleChangePage={handleChangePage}
                  handleChangeRowsPerPage={handleChangeRowsPerPage}
                  loading={myntraData?.marketingHistory?.loading}
                />
              </CardContent>
            </Card>
          )}

          {/* Row Action Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                if (selectedRow?._id) {
                  setRowDetails(selectedRow);
                  setDetailsDialog(true);
                }
              }}
            >
              <ViewIcon sx={{ mr: 1 }} />
              View Details
            </MenuItem>
          </Menu>

          {/* Details Dialog */}
          <Dialog
            open={detailsDialog}
            onClose={() => setDetailsDialog(false)}
            maxWidth="lg"
            fullWidth
          >
            <DialogTitle>Import History Details</DialogTitle>
            <DialogContent dividers>
              <Box mb={2}>
                <Typography variant="subtitle1" fontWeight={600}>
                  File: {rowDetails?.originalFileName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Imported By: <strong>{rowDetails?.importedBy?.name}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Date:{" "}
                  {moment
                    .utc(rowDetails?.createdAt)
                    .format("DD/MM/YYYY hh:mm A")}
                </Typography>
              </Box>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                {statCards.map((item, index) => (
                  <Grid key={index} item xs={6} sm={4} md={2}>
                    <Card
                      sx={{
                        textAlign: "center",
                        borderRadius: 3,
                        background:
                          item.color === "success"
                            ? "#E8F5E9"
                            : item.color === "error"
                            ? "#FFEBEE"
                            : item.color === "warning"
                            ? "#FFF8E1"
                            : item.color === "info"
                            ? "#E3F2FD"
                            : "#F5F5F5",
                      }}
                      elevation={0}
                    >
                      <CardContent>
                        <Box display="flex" justifyContent="center" mb={1}>
                          {item.icon}
                        </Box>
                        <Typography variant="h6" fontWeight={700}>
                          {item?.value ?? 0}
                        </Typography>
                        <Typography variant="body2">{item?.label}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {rowDetails?.issueDetails?.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Error Details:
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflow: "auto" }}>
                    {rowDetails.issueDetails.map((err, i) => (
                      <Chip
                        key={i}
                        label={`Row ${err?.row}: ${err?.error}`}
                        color="error"
                        variant="outlined"
                        size="small"
                        sx={{ m: 0.5 }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailsDialog(false)}>Close</Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </Box>
  );
};

export default MyntraMarketingExpenses;
